// src/hooks/useShopLink.ts
//
// "Shop the collection" CTAs used to hardcode `/shop/apparel`. That string is
// a Square catalog category name, so renaming the top-level category in Square
// silently turned every one of those buttons into a dead end — there is no
// bare `/shop` route to fall back to (MainPage only registers `/shop/:domain`),
// and nothing errors, the grid just comes up empty.
//
// Resolve the destination from the catalog the browser already has instead.
// A "domain" is a top-level category WITH children — the same test
// TabDropdownList uses to decide what goes in the nav — which also skips the
// seasonal themes (top-level, no children) that `getDomains()` returns too.
import { useCatalog } from "../contexts/CatalogContext";
import { getTabLink } from "../extensions/helpers";

// Only used before the catalog finishes loading (first visit, cache expired).
// If this is ever wrong the CTA is no worse off than it was hardcoded.
const FALLBACK_DOMAIN = "Apparel";

/**
 * Name of the primary shoppable top-level category, for display in copy.
 *
 * ⚠️ ARBITRARY WITH MORE THAN ONE DOMAIN. `find` takes whichever domain Square
 * returns first, and that order isn't controlled from the Square product
 * screen. With a single shoppable category (the case as of 2026-07-24) this is
 * exact. **Add a second one and the generic CTAs — "Shop the Collection",
 * "Browse the Shop", "Shop Now" — may silently start pointing at it instead**,
 * because none of them name a category, so something has to choose.
 *
 * That's a decision to make deliberately when the second category lands, not a
 * bug to patch blindly. Three options, roughly in order of effort:
 *   1. Leave it — fine if the categories are genuine peers and it doesn't
 *      matter which one a generic button opens.
 *   2. Mark a primary — pick the domain these always use (closest to the old
 *      hardcoded behaviour, and the most predictable).
 *   3. Point them at a shop index listing all categories — cleanest, but no
 *      such page or route exists today, so it's a build rather than a tweak.
 *
 * See docs/catalog-change-checklist.md § Adding a new top-level category.
 */
export const useShopDomainName = (): string => {
  const { getDomains, getChildCategories } = useCatalog();

  return (
    getDomains()?.find((d) => getChildCategories(d.id)?.length > 0)
      ?.categoryData?.name ?? FALLBACK_DOMAIN
  );
};

/**
 * Link to the shop. Pass a child category name to deep-link into a product
 * line; if that category no longer exists (renamed, deleted, not yet loaded)
 * the link degrades to the domain rather than pointing at nothing.
 */
export const useShopLink = (categoryName?: string): string => {
  const { getCategoryByName, getCategoryById } = useCatalog();
  const defaultDomain = useShopDomainName();

  const category = categoryName ? getCategoryByName(categoryName) : undefined;
  // Trust the category's own parent over the default domain — a product line
  // may well live under a different top-level category than the primary one.
  const parentId = category?.categoryData?.parentCategory?.id;
  const domain = parentId
    ? (getCategoryById(parentId)?.categoryData?.name ?? defaultDomain)
    : defaultDomain;

  return getTabLink({
    domain,
    category: category?.categoryData?.name ?? undefined,
  });
};
